import { Button } from "@design";
import { useTranslation } from "react-i18next";
import type { MergeRecord } from "@/api";
import { Text } from "@/components/Text";
import { formatDateTime } from "@/i18n/format";
import { useUndoMerge } from "./useDuplicates";

/** The space's recent merges (the audit trail), each undoable until undone. */
export function MergeHistory({ spaceUuid, merges }: Readonly<{ spaceUuid: string; merges: MergeRecord[] }>) {
  const { t } = useTranslation("knowledge");
  const undo = useUndoMerge(spaceUuid);
  if (merges.length === 0) return null;
  return (
    <section aria-label={t("duplicates.history")} className="flex flex-col gap-2">
      <Text variant="h3" as="h2">
        {t("duplicates.history")}
      </Text>
      <ul className="flex flex-col divide-y divide-border-default">
        {merges.map((m) => (
          <li key={m.uuid} className="flex flex-wrap items-center justify-between gap-2 py-2">
            <div className="flex min-w-0 flex-col">
              <Text variant="body-sm">
                {t(m.undone ? "duplicates.undoneItem" : "duplicates.historyItem", {
                  removed: m.removedName,
                  kept: m.keptName,
                })}
              </Text>
              <Text variant="caption" tone="tertiary">
                {formatDateTime(m.createdAt)}
              </Text>
            </div>
            {!m.undone && (
              <Button
                variant="secondary"
                size="sm"
                loading={undo.isPending && undo.variables?.path.merge_uuid === m.uuid}
                onClick={() => undo.mutate({ path: { space_uuid: spaceUuid, merge_uuid: m.uuid } })}
              >
                {t("merge.undo")}
              </Button>
            )}
          </li>
        ))}
      </ul>
    </section>
  );
}
